import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AgGridTable, Button, Pagination, DateRangePicker } from 'components/common';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { MdCalendarMonth, MdFilterAlt } from 'react-icons/md';
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import {
  getMasterSchedules,
  exportData as exportMasterSchedule,
  setNestedParams,
} from 'redux/crm/report/masterScheduleSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import { columnDefs as originalColumnDefs } from 'components/app/crm/reports/project_master_schedule/Column';
import MasterScheduleFilter from './MasterScheduleFilter';
import { debounce } from 'helpers/debounce';

const SEARCH_PARAM = 'project_q_number_or_project_j_number_or_project_name_cont';

const RegionPicker = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, left: 0 });
  const triggerRef      = useRef(null);
  const dropdownRef     = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen((o) => !o);
  };

  const toggle = (val) =>
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);

  const isActive = value.length > 0;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggleOpen}
        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-cursor-pointer tw-transition-all tw-whitespace-nowrap tw-flex-shrink-0 ${
          isActive
            ? 'tw-bg-primary-50 tw-border-primary-300 tw-text-primary-700'
            : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
        }`}
      >
        <span>{isActive ? `${value.length} region${value.length > 1 ? 's' : ''}` : 'Sales Region'}</span>
        {isActive && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            onKeyDown={(e) => e.key === 'Enter' && onChange([])}
            className="tw-flex tw-items-center tw-justify-center tw-w-3.5 tw-h-3.5 tw-rounded-xl tw-bg-primary-200 hover:tw-bg-red-200 tw-cursor-pointer tw-transition-colors"
          >
            <FaTimes size={7} className="tw-text-primary-700" />
          </span>
        )}
      </button>

      {open && ReactDOM.createPortal(
        <>
          <div className="tw-fixed tw-inset-0 tw-z-[9998]" onClick={() => setOpen(false)} />
          <div
            ref={dropdownRef}
            className="tw-fixed tw-z-[9999] tw-bg-white tw-rounded tw-shadow-xl tw-border tw-border-slate-200 tw-py-1 tw-min-w-[180px] tw-max-h-64 tw-overflow-y-auto"
            style={{ top: pos.top, left: pos.left }}
          >
            {options.map((opt) => (
              <label
                key={opt.value}
                className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 hover:tw-bg-primary-50 tw-cursor-pointer tw-text-sm tw-text-slate-700 tw-select-none"
              >
                <input
                  type="checkbox"
                  checked={value.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                  className="tw-accent-primary-600 tw-w-4 tw-h-4 tw-flex-shrink-0"
                />
                <span>{opt.label}</span>
              </label>
            ))}
            {value.length > 0 && (
              <>
                <div className="tw-h-px tw-bg-slate-100 tw-mx-2 tw-my-1" />
                <button
                  type="button"
                  onClick={() => { onChange([]); setOpen(false); }}
                  className="tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-xs tw-text-red-500 hover:tw-bg-red-50 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                >
                  Clear all
                </button>
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

RegionPicker.propTypes = {
  options:  PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  value:    PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

const MasterProjectSchedulePage = () => {
  const dispatch = useDispatch();

  const rowData              = useSelector((s) => s.crm_report_master_schedule.allData.data);
  const totalRows            = useSelector((s) => s.crm_report_master_schedule.allData.totalRows);
  const loading              = useSelector((s) => s.crm_report_master_schedule.loading);
  const exporting            = useSelector((s) => s.crm_report_master_schedule.exporting);
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort]       = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]   = useState('');
  const [activeFilterParams, setActiveFilterParams] = useState({});
  const [filterTags, setFilterTags] = useState([]);
  const [showFilter, setShowFilter] = useState(false);

  // Quick filter state (visible inline in filter bar)
  const [quickStatus, setQuickStatus]               = useState('');
  const [quickContractDate, setQuickContractDate]   = useState({ startDate: '', endDate: '' });
  const [quickDownPayment, setQuickDownPayment]               = useState({ startDate: '', endDate: '' });
  const [quickPurchaseOrderDate, setQuickPurchaseOrderDate]   = useState({ startDate: '', endDate: '' });
  const [quickSalesRegions, setQuickSalesRegions]             = useState([]);

  const _y = new Date().getFullYear();
  const THIS_YEAR = { startDate: `${_y}-01-01`, endDate: `${_y}-12-31` };

  const [quickJobEntry, setQuickJobEntry] = useState(THIS_YEAR);

  const salesRegionOptions = useMemo(
    () =>
      Array.from(new Set(activeSalesUnitsData.filter((u) => u.region).map((u) => u.region)))
        .sort()
        .map((r) => ({ value: r, label: r })),
    [activeSalesUnitsData],
  );

  const searchRef             = useRef('');
  const activeFilterParamsRef = useRef({});
  const quickFiltersRef       = useRef({
    status: '',
    jobEntry: THIS_YEAR,
    contractDate: { startDate: '', endDate: '' },
    downPayment: { startDate: '', endDate: '' },
    purchaseOrderDate: { startDate: '', endDate: '' },
    salesRegions: [],
  });

  // Reads from ref — always returns the latest quick filter ransack params
  const buildQuickFilterParams = () => {
    const { status, jobEntry, contractDate, downPayment, purchaseOrderDate, salesRegions } = quickFiltersRef.current;
    const p = {};
    if (status !== '') p.active_true = status;
    if (jobEntry.startDate) p.job_entry_date_gteq = jobEntry.startDate;
    if (jobEntry.endDate) p.job_entry_date_lteq = jobEntry.endDate;
    if (contractDate.startDate) p.contract_date_gteq = contractDate.startDate;
    if (contractDate.endDate) p.contract_date_lteq = contractDate.endDate;
    if (downPayment.startDate) p.down_payment_gteq = downPayment.startDate;
    if (downPayment.endDate) p.down_payment_lteq = downPayment.endDate;
    if (purchaseOrderDate.startDate) p.purchase_order_date_gteq = purchaseOrderDate.startDate;
    if (purchaseOrderDate.endDate) p.purchase_order_date_lteq = purchaseOrderDate.endDate;
    if (salesRegions.length > 0) p.sales_unit_region_in = salesRegions;
    return p;
  };

  const totalPages = Math.ceil(totalRows / perPage) || 1;

  const buildParams = useCallback(
    (overrides = {}) => ({
      page,
      per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
      ...overrides,
    }),
    [page, perPage, sort],
  );

  const fetchData = useCallback(
    (params) => {
      dispatch(setNestedParams({ key: 'allData', params }));
      dispatch(getMasterSchedules(params));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(getActiveSalesUnits());
  }, [dispatch]);

  useEffect(() => {
    fetchData(buildParams());
  }, [fetchData, buildParams]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    setPage(1);
    debouncedFetch({
      page: 1,
      per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(val && { [SEARCH_PARAM]: val }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    setPage(1);
    fetchData({
      page: 1, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir
      ? { attribute: colId, direction: dir }
      : { attribute: 'id', direction: 'desc' };
    setSort(newSort);
    setPage(1);
    fetchData({
      page: 1,
      per_page: perPage,
      sort: newSort,
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  }, [fetchData, perPage]);

  const handleApplyFilter = (params, tags) => {
    activeFilterParamsRef.current = params;
    setActiveFilterParams(params);
    setFilterTags(tags);
    setPage(1);
    setShowFilter(false);
    fetchData({
      page: 1,
      per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...params,
    });
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {};
    quickFiltersRef.current = {
      status: '',
      jobEntry: { startDate: '', endDate: '' },
      contractDate: { startDate: '', endDate: '' },
      downPayment: { startDate: '', endDate: '' },
      purchaseOrderDate: { startDate: '', endDate: '' },
      salesRegions: [],
    };
    setActiveFilterParams({});
    setFilterTags([]);
    setQuickStatus('');
    setQuickJobEntry({ startDate: '', endDate: '' });
    setQuickContractDate({ startDate: '', endDate: '' });
    setQuickDownPayment({ startDate: '', endDate: '' });
    setQuickPurchaseOrderDate({ startDate: '', endDate: '' });
    setQuickSalesRegions([]);
    setPage(1);
    fetchData({
      page: 1,
      per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
    });
  };

  const removeTag = (tag) => {
    const DATE_RANGE_PAIRS = {
      down_payment_gteq:        ['down_payment_gteq', 'down_payment_lteq'],
      down_payment_lteq:        ['down_payment_gteq', 'down_payment_lteq'],
      purchase_order_date_gteq: ['purchase_order_date_gteq', 'purchase_order_date_lteq'],
      purchase_order_date_lteq: ['purchase_order_date_gteq', 'purchase_order_date_lteq'],
    };
    const keysToRemove = new Set(DATE_RANGE_PAIRS[tag.id] || [tag.id]);
    const updatedParams = Object.fromEntries(
      Object.entries(activeFilterParams).filter(([k]) => !keysToRemove.has(k)),
    );
    activeFilterParamsRef.current = updatedParams;
    setActiveFilterParams(updatedParams);
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    fetchData({
      page: 1,
      per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...updatedParams,
    });
  };

  // ── Quick filter handlers ────────────────────────────────────────────────

  const handleQuickStatusChange = (val) => {
    quickFiltersRef.current = { ...quickFiltersRef.current, status: val };
    setQuickStatus(val);
    setPage(1);
    fetchData({
      page: 1, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  };

  const handleQuickJobEntryChange = ({ startDate, endDate }) => {
    quickFiltersRef.current = { ...quickFiltersRef.current, jobEntry: { startDate, endDate } };
    setQuickJobEntry({ startDate, endDate });
    setPage(1);
    fetchData({
      page: 1, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  };

  const handleQuickContractDateChange = ({ startDate, endDate }) => {
    quickFiltersRef.current = { ...quickFiltersRef.current, contractDate: { startDate, endDate } };
    setQuickContractDate({ startDate, endDate });
    setPage(1);
    fetchData({
      page: 1, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  };

  const handleQuickDownPaymentChange = ({ startDate, endDate }) => {
    quickFiltersRef.current = { ...quickFiltersRef.current, downPayment: { startDate, endDate } };
    setQuickDownPayment({ startDate, endDate });
    setPage(1);
    fetchData({
      page: 1, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  };

  const handleQuickPurchaseOrderDateChange = ({ startDate, endDate }) => {
    quickFiltersRef.current = { ...quickFiltersRef.current, purchaseOrderDate: { startDate, endDate } };
    setQuickPurchaseOrderDate({ startDate, endDate });
    setPage(1);
    fetchData({
      page: 1, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  };

  const handleQuickSalesRegionsChange = (regions) => {
    quickFiltersRef.current = { ...quickFiltersRef.current, salesRegions: regions };
    setQuickSalesRegions(regions);
    setPage(1);
    fetchData({
      page: 1, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_PARAM]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div
              className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0"
              style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }}
            />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdCalendarMonth className="tw-text-emerald-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">
                  Master Project Schedule
                </h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">
                  Complete master schedule of all J-type project buildings.
                </p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <Button
              variant="secondary"
              size="md"
              leftIcon={FaSyncAlt}
              loading={loading}
              title="Reload"
              onClick={() => fetchData(buildParams())}
            >
              Reload
            </Button>

            <Button
              variant="secondary"
              size="md"
              leftIcon={FaFileExcel}
              disabled={exporting || loading}
              onClick={() => dispatch(exportMasterSchedule(buildParams()))}
              className="tw-text-green-700 tw-border-green-300 tw-bg-green-50 hover:tw-bg-green-100"
            >
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        {/* Controls row */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

          {/* Search input */}
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-48 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Q#, J#, name..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0"
              >
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Status quick filter */}
          <div className="tw-flex tw-items-center tw-rounded tw-border tw-border-slate-200 tw-overflow-hidden tw-flex-shrink-0 tw-text-xs tw-font-medium">
            {[['', 'All'], [true, 'Active'], [false, 'Deactive']].map(([val, label]) => (
              <button
                key={String(val)}
                type="button"
                onClick={() => handleQuickStatusChange(val)}
                className={`tw-px-2.5 tw-py-1.5 tw-border-0 tw-cursor-pointer tw-transition-colors ${
                  quickStatus === val
                    ? 'tw-bg-primary-600 tw-text-white'
                    : 'tw-bg-white tw-text-slate-600 hover:tw-bg-primary-50 hover:tw-text-primary-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Job Entry Date quick filter */}
          <div className="tw-flex-shrink-0">
            <DateRangePicker
              label="Job Entry"
              startDate={quickJobEntry.startDate}
              endDate={quickJobEntry.endDate}
              onChange={handleQuickJobEntryChange}
            />
          </div>

          {/* Contract Date quick filter */}
          <div className="tw-flex-shrink-0">
            <DateRangePicker
              label="Contract Date"
              startDate={quickContractDate.startDate}
              endDate={quickContractDate.endDate}
              onChange={handleQuickContractDateChange}
            />
          </div>

          {/* Down Payment Date quick filter */}
          <div className="tw-flex-shrink-0">
            <DateRangePicker
              label="Down Pmt"
              startDate={quickDownPayment.startDate}
              endDate={quickDownPayment.endDate}
              onChange={handleQuickDownPaymentChange}
            />
          </div>

          {/* Purchase Order Date quick filter */}
          <div className="tw-flex-shrink-0">
            <DateRangePicker
              label="PO Date"
              startDate={quickPurchaseOrderDate.startDate}
              endDate={quickPurchaseOrderDate.endDate}
              onChange={handleQuickPurchaseOrderDateChange}
            />
          </div>

          {/* Sales Region quick filter */}
          {salesRegionOptions.length > 0 && (
            <RegionPicker
              options={salesRegionOptions}
              value={quickSalesRegions}
              onChange={handleQuickSalesRegionsChange}
            />
          )}

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* More Filters button */}
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              filterTags.length > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            More Filters
            {filterTags.length > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {filterTags.length}
              </span>
            )}
          </button>

          {/* Record count */}
          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {/* Active drawer filter tags row */}
        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span
                key={tag.id}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm"
              >
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">
                  {Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}
                </span>
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors"
                >
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <Button
              variant="secondary"
              size="xs"
              leftIcon={FaTimes}
              onClick={handleResetFilter}
              className="tw-text-red-500 tw-border-red-200 tw-bg-transparent hover:tw-bg-red-500 hover:tw-text-white hover:tw-border-red-500"
            >
              Clear all
            </Button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable
          rowData={rowData || []}
          columnDefs={originalColumnDefs}
          loading={loading}
          height="max(320px, calc(100dvh - 290px))"
          pagination={false}
          filter={false}
          onSortChanged={handleSort}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />

      <MasterScheduleFilter
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        activeParams={activeFilterParams}
      />
    </div>
  );
};

export default MasterProjectSchedulePage;
